import type { Fund, Holding, MarketLens, ExposureItem, MarketSignal, Period } from './types'

export type AutomaticUniverseResponse = {
  provider: 'TEFAS'
  source: string
  funds: Fund[]
  total: number
  refreshedAt: string
  asOf: string
  stale?: boolean
  staleError?: string
}

export type AutomaticExposureResponse = {
  provider: 'TEFAS'
  code: string
  asOf: string
  source: string
  assets: ExposureItem[]
  sectors: ExposureItem[]
  holdings: Holding[]
  marketLenses: MarketLens[]
  recent: { daily: number | null; weekly: number | null; currentDrawdown: number | null; price: number | null; fundSize: number | null; investors: number | null; returns: Partial<Record<Period, number | null>>; asOf: string }
  coverage: { assetBreakdown: boolean; sectorBreakdown: boolean; securityHoldings: boolean }
  note: string
}

export type MarketSignalsResponse = {
  provider: string
  fetchedAt: string
  items: MarketSignal[]
}

function requestJson<T>(path: string): Promise<T> {
  return fetch(path, { headers: { Accept: 'application/json' } }).then(async (response) => {
    const payload = await response.json().catch(() => ({})) as T & { error?: string }
    if (!response.ok) throw new Error(payload.error || `Automatic data request failed (${response.status})`)
    return payload
  })
}

export function fetchAutomaticUniverse(): Promise<AutomaticUniverseResponse> {
  return requestJson<AutomaticUniverseResponse>('/api/funds')
}

export function fetchFundExposure(code: string): Promise<AutomaticExposureResponse> {
  return requestJson<AutomaticExposureResponse>(`/api/funds/${encodeURIComponent(code)}/exposure`)
}

export function fetchMarketSignals(): Promise<MarketSignalsResponse> {
  return requestJson<MarketSignalsResponse>('/api/market')
}

export function liveSourceLabel(fund: Fund): string {
  return fund.automatic?.provider === 'TEFAS' ? `TEFAS automatic · ${fund.automatic.asOf}` : 'automatic analytics'
}
